<script module>
    export const route = "/approvals/:taskId";
    export const sessionRequired = false;
</script>

<script lang="ts">
    import MailboxThread from "../lib/components/mailbox-thread.svelte";
    import AssistantIframe from "../lib/components/assistant-iframe.svelte";
    import { definedOrDie, notNullOrDie, z } from "@repo/utils";
    import { getClient } from "../lib/utils.js";
    import { useURL } from "../lib/url.svelte.js";
    import { useRoute } from "../lib/route.svelte.js";
    import { m } from "../lib/messages.svelte.js";
    import { Button } from "../lib/components/ui/button";
    import { onMount } from "svelte";
    import CircleLoader from "../lib/components/circle-loader.svelte";
    import type { ApprovalTaskId } from "@repo/backend/types";

    const url = useURL({ token: z.string() });
    const route = useRoute();
    const token = $derived(definedOrDie(url.query.token));
    const approvalTaskId = $derived(notNullOrDie(route.current).params["taskId"] as ApprovalTaskId);

    let pageState = $state<"loading" | "waitingToken" | "reissuing" | "reissued" | "cancelled" | null>("loading");

    async function resendAccessToken() {
        pageState = "reissuing";

        await getClient()["Resend approval task"]({
            query: { token },
            params: { approvalTaskId },
        });

        pageState = "reissued";
    }

    onMount(async () => {
        const task = await getClient()["Get approval task"]({
            params: { approvalTaskId },
            query: { token },
        });

        pageState = task.active ? null : "cancelled";
    });
</script>

{#if pageState === null}
    <div class="flex h-full grow overflow-x-hidden">
        <div class="flex h-full grow overflow-x-hidden">
            <div class="grow overflow-x-hidden border-r p-3">
                <MailboxThread {approvalTaskId} approvalTaskToken={token} />
            </div>
            <div class="relative h-screen flex-[0_0_400px]">
                <AssistantIframe
                    data={{
                        threadId: null,
                        approvalTaskId,
                        token,
                    }}
                />
            </div>
        </div>
    </div>
{:else if pageState === "loading"}
    <CircleLoader class="h-20 w-20" />
{:else}
    <div class="flex h-screen items-center justify-center text-xl">
        {#if pageState === "reissued"}
            <div>{m["approval.tokenReissuedMessage"]()}</div>
        {:else if pageState === "cancelled"}
            <div>{m["approval.taskCancelledMessage"]()}</div>
        {:else}
            <div class="flex flex-col items-center gap-4">
                <div>{m["approval.tokenExpirationMessage"]()}</div>
                <Button class="px-10 py-7 text-lg" disabled={pageState === "reissuing"} onclick={resendAccessToken}
                    >{m["approval.issueNewToken"]()}</Button
                >
            </div>
        {/if}
    </div>
{/if}
